import { useState, useEffect } from 'react';
import { useLocation, Outlet, useNavigate, Link } from 'react-router-dom';
import {
  Home, Database, Search, Bell, Settings as SettingsIcon, LogOut,
  Menu, X, Code, Hash, Globe, Settings, Folder, Tag, Plus, PlusSquare,
  FileCode2, Cloud, Lock, Users, Download, Shield, Building, ChevronRight,
  Trash2, FolderPlus, FilePlus, ChevronDown, Layers, HelpCircle, CreditCard, LayoutDashboard, Terminal
} from 'lucide-react';
import { useAuth } from '@snipit/core/context/AuthContext';
import { useOrganization } from '@snipit/core/hooks/useOrganization';
import { useProjects } from '@snipit/core/hooks/useProjects';
import Logo from '@snipit/ui/components/Logo';
import OptimizedImage from '@snipit/ui/components/OptimizedImage';
import { trackEvent } from '@snipit/core/services/analytics';

export default function DashboardLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { userProfile, logout, userLicense, currentUser } = useAuth();
  const { orgMember } = useOrganization();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [currentVault, setCurrentVault] = useState('dev');
  const [isDeletingProjects, setIsDeletingProjects] = useState(false);
  const [selectedProjectIds, setSelectedProjectIds] = useState(new Set());

  const { projectTree, addProject, removeProjects, toggleProjectExpansion } = useProjects();

  const [addFolderModalOpen, setAddFolderModalOpen] = useState(false);
  const [addTypeModalOpen, setAddTypeModalOpen] = useState(false);
  const [activeParentId, setActiveParentId] = useState(null);
  const [newFolderName, setNewFolderName] = useState('');

  const handleAddFolderSubmit = async (e) => {
    e.preventDefault();
    if (!newFolderName.trim()) return;
    await addProject({ name: newFolderName, parentId: activeParentId });
    setNewFolderName('');
    setAddFolderModalOpen(false);
    setActiveParentId(null);
  };

  const handleConfirmDelete = async () => {
    if (selectedProjectIds.size > 0) {
      await removeProjects(Array.from(selectedProjectIds));
      setSelectedProjectIds(new Set());
    }
    setIsDeletingProjects(false);
  };

  const renderProjectTree = (nodes, depth = 0) => {
    return nodes.map(project => (
      <div key={project.id}>
        <div 
          className={`flex items-center justify-between px-3 py-1.5 rounded-md cursor-pointer transition-colors text-sm ${
            project.id === '1' ? 'bg-dev-blue/10 text-dev-blue font-medium' : 'text-text-muted hover:bg-surface-container hover:text-white'
          }`}
          style={{ paddingLeft: depth === 0 ? '0.75rem' : `${depth * 1 + 0.75}rem` }}
        >
          <div className="flex items-center gap-1.5" onClick={() => toggleProjectExpansion(project.id, project.isExpanded)}>
            {isDeletingProjects && (
              <input 
                type="checkbox" 
                checked={selectedProjectIds.has(project.id)}
                onChange={(e) => {
                  const newSet = new Set(selectedProjectIds);
                  if (e.target.checked) newSet.add(project.id);
                  else newSet.delete(project.id);
                  setSelectedProjectIds(newSet);
                }}
                onClick={(e) => e.stopPropagation()} 
                className="mr-1 accent-error cursor-pointer rounded border-border-muted bg-surface-container-low"
              />
            )}
            {project.children && project.children.length > 0 ? (
              project.isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />
            ) : (
              <span className="w-3 h-3 inline-block"></span>
            )}
            <Folder className={`w-3.5 h-3.5 ${project.id === '1' ? 'text-dev-blue' : 'text-text-hint'}`} />
            <span className="truncate">{project.name}</span>
          </div>
          <div className="flex items-center gap-2 group/btn">
            {isDeletingProjects ? (
              <button 
                onClick={async (e) => {
                  e.stopPropagation();
                  if (window.confirm(`Delete project: ${project.name}?`)) {
                    await removeProjects([project.id]);
                  }
                }}
                className="opacity-0 group-hover/btn:opacity-100 p-0.5 hover:bg-error/20 rounded text-error transition-all"
                title="Delete Folder"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            ) : (
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  if (depth >= 3) {
                    alert('Maximum folder depth reached.');
                    return;
                  }
                  setActiveParentId(project.id);
                  setAddTypeModalOpen(true);
                }}
                className="opacity-0 group-hover/btn:opacity-100 p-0.5 hover:bg-surface-container-high rounded text-text-hint hover:text-white transition-all"
                title="Add Item"
              >
                <Plus className="w-3 h-3" />
              </button>
            )}
            <span className="text-[10px] bg-(--color-surface) px-1.5 py-0.5 rounded-full border border-border-muted/50">{project.count}</span>
          </div>
        </div>
        {project.isExpanded && project.children && (
          <div className="mt-0.5 space-y-0.5">
            {renderProjectTree(project.children, depth + 1)}
          </div>
        )}
      </div>
    ));
  };

  useEffect(() => {
    if (window.chrome && window.chrome.storage && window.chrome.storage.local) {
      chrome.storage.local.get(['currentVault'], (res) => {
        if (res.currentVault) setCurrentVault(res.currentVault);
      });
    }

    const handleVaultChange = (e) => setCurrentVault(e.detail);
    window.addEventListener('vaultChanged', handleVaultChange);
    
    return () => window.removeEventListener('vaultChanged', handleVaultChange);
  }, []);

  const workspaceItems = [
    { name: 'Dashboard', href: '/home', icon: Home },
    { name: 'My Vaults', href: '/vault', icon: Database },
  ];

  if (userLicense?.tier === 'premium' || userLicense?.tier === 'enterprise' || userLicense?.tier === 'enterprise_org') {
    workspaceItems.push({ name: 'Code Gallery', href: '/gallery', icon: Globe });
  }

  workspaceItems.push({ name: 'Dev Board', href: '/community', icon: Users });
  workspaceItems.push({ name: 'Downloads', href: '/downloads', icon: Download });

  if (userLicense?.tier === 'enterprise' || userLicense?.tier === 'enterprise_org') {
    workspaceItems.push({ name: 'AI Review', href: '/review', icon: Code });
  }

  if (userLicense?.tier === 'enterprise_org' && orgMember) {
    workspaceItems.push({ name: 'Teams', href: '/teams', icon: Users });
  }

  if ((orgMember && (orgMember.role === 'admin' || orgMember.role === 'Administrator')) || userProfile?.isAdmin) {
    workspaceItems.push({ name: 'Organization', href: '/org-admin', icon: Building });
  }

  return (
    <div className="flex flex-col h-screen bg-bg-main text-text-primary font-sans overflow-hidden">

      {/* Top App Bar */}
      <nav className="bg-(--color-surface)/ backdrop-blur-md border-b border-border-muted shadow-xl flex justify-between items-center h-14 px-6 fixed w-full z-50 transition-colors duration-300">

        {/* Left: Logo */}
        <div className="flex items-center gap-6 w-[200px]">
          <Link to="/home" className="flex items-center gap-2 group cursor-pointer transition-transform hover:scale-105">
            <Logo vault={currentVault} className="w-8 h-8 transition-all duration-300" />
            <div className="flex items-baseline font-bold tracking-tight text-[20px] font-['Geist']">
              <span className="text-text-primary">Snip</span>
              <span className={`transition-colors duration-300 ${
                  currentVault === 'dev' ? 'text-dev-blue' :
                  currentVault === 'ai' ? 'text-ai-purple' :
                  currentVault === 'links' ? 'text-links-emerald' :
                  currentVault === 'tools' ? 'text-tools-amber' :
                  'text-blue-400'
              }`}>
                IT
              </span>
            </div>
          </Link>
        </div>

        {/* Middle: Navigation Dropdowns (Centered) */}
        <div className="hidden md:flex flex-1 justify-center">
          <div className="flex items-center gap-2 bg-surface-container-low/80 backdrop-blur-md p-1 rounded-full border border-border-muted/50 shadow-sm">
            {/* Workspace Dropdown */}
            <div className="relative group">
              <button className="flex items-center gap-1.5 text-sm font-medium text-text-muted hover:text-white transition-all duration-200 py-1.5 px-4 rounded-full hover:bg-(--color-surface-container-highest)">
                <Layers className="w-3.5 h-3.5" /> Workspace <ChevronRight className="w-3 h-3 group-hover:rotate-90 transition-transform opacity-50" />
              </button>
              <div className="absolute top-full left-0 mt-2 w-48 bg-(--color-surface) border border-border-muted rounded-xl shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 py-1.5 overflow-hidden z-50">
                <Link to="/home" className="flex items-center gap-2 px-3 py-2 text-sm text-text-muted hover:bg-surface-container hover:text-white transition-colors">
                  <Home className="w-4 h-4" /> Dashboard
                </Link>
                <Link to="/vault" className="flex items-center gap-2 px-3 py-2 text-sm text-text-muted hover:bg-surface-container hover:text-white transition-colors">
                  <Database className="w-4 h-4" /> My Vaults
                </Link>
              </div>
            </div>

            {/* Community Dropdown */}
            <div className="relative group">
              <button className="flex items-center gap-1.5 text-sm font-medium text-text-muted hover:text-white transition-all duration-200 py-1.5 px-4 rounded-full hover:bg-(--color-surface-container-highest)">
                <Globe className="w-3.5 h-3.5" /> Community <ChevronRight className="w-3 h-3 group-hover:rotate-90 transition-transform opacity-50" />
              </button>
              <div className="absolute top-full left-0 mt-2 w-48 bg-(--color-surface) border border-border-muted rounded-xl shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 py-1.5 overflow-hidden z-50">
                {(userLicense?.tier === 'premium' || userLicense?.tier === 'enterprise' || userLicense?.tier === 'enterprise_org') && (
                  <Link to="/gallery" className="flex items-center gap-2 px-3 py-2 text-sm text-text-muted hover:bg-surface-container hover:text-white transition-colors">
                    <Globe className="w-4 h-4" /> Code Gallery
                  </Link>
                )}
                <Link to="/community" className="flex items-center gap-2 px-3 py-2 text-sm text-text-muted hover:bg-surface-container hover:text-white transition-colors">
                  <Users className="w-4 h-4" /> Dev Board
                </Link>
                <Link to="/downloads" className="flex items-center gap-2 px-3 py-2 text-sm text-text-muted hover:bg-surface-container hover:text-white transition-colors">
                  <Download className="w-4 h-4" /> Downloads
                </Link>
              </div>
            </div>

            {/* Enterprise Dropdown */}
            {(userLicense?.tier === 'enterprise' || userLicense?.tier === 'enterprise_org' || (orgMember && (orgMember.role === 'admin' || orgMember.role === 'Administrator')) || userProfile?.isAdmin) && (
              <div className="relative group">
                <button className="flex items-center gap-1.5 text-sm font-medium text-text-muted hover:text-white transition-all duration-200 py-1.5 px-4 rounded-full hover:bg-(--color-surface-container-highest)">
                  <Shield className="w-3.5 h-3.5" /> Enterprise <ChevronRight className="w-3 h-3 group-hover:rotate-90 transition-transform opacity-50" />
                </button>
                <div className="absolute top-full left-0 mt-2 w-48 bg-(--color-surface) border border-border-muted rounded-xl shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 py-1.5 overflow-hidden z-50">
                  {(userLicense?.tier === 'enterprise' || userLicense?.tier === 'enterprise_org') && (
                    <Link to="/review" className="flex items-center gap-2 px-3 py-2 text-sm text-text-muted hover:bg-surface-container hover:text-white transition-colors">
                      <Code className="w-4 h-4" /> AI Review
                    </Link>
                  )}
                  {userLicense?.tier === 'enterprise_org' && orgMember && (
                    <Link to="/teams" className="flex items-center gap-2 px-3 py-2 text-sm text-text-muted hover:bg-surface-container hover:text-white transition-colors">
                      <Users className="w-4 h-4" /> Teams
                    </Link>
                  )}
                  {((orgMember && (orgMember.role === 'admin' || orgMember.role === 'Administrator')) || userProfile?.isAdmin) && (
                    <Link to="/org-admin" className="flex items-center gap-2 px-3 py-2 text-sm text-text-muted hover:bg-surface-container hover:text-white transition-colors">
                      <Building className="w-4 h-4" /> Organization
                    </Link>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-2 justify-end shrink-0 w-[200px]">
          <button onClick={() => navigate('/settings')} className="text-text-muted hover:text-white hover:bg-surface-container transition p-2 rounded-full flex flex-col items-center group relative mr-1" title="Settings">
            <Settings size={20} className="group-hover:rotate-45 transition-transform" />
          </button>
          
          <button onClick={() => {
            navigate('/vault');
            setTimeout(() => window.dispatchEvent(new CustomEvent('openHelp')), 100);
          }} className="text-text-muted hover:text-white hover:bg-surface-container transition p-2 rounded-full flex flex-col items-center group" title="Help & Documentation">
            <HelpCircle size={20} className="group-hover:scale-110 transition-transform" />
          </button>
          
          <div className="h-6 w-px bg-border-muted mx-1 hidden md:block"></div>
          
          <button onClick={logout} className="text-text-muted hover:text-error hover:bg-error/20 transition p-2 rounded-full flex flex-col items-center group" title="Logout">
            <LogOut size={20} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </nav>

      {/* Main Container */}
      <div className="flex pt-14 h-screen bg-bg-main overflow-hidden">

        {/* Left Sidebar */}
        <aside className="w-16 md:w-72 bg-(--color-surface)/ border-r border-border-muted flex flex-col shrink-0 transition-all duration-300 relative overflow-y-auto overflow-x-hidden backdrop-blur-sm z-40">

          <nav className="flex-1 py-6 px-4 space-y-8 overflow-y-auto custom-scrollbar">



            {/* Sidebar Content based on Route */}
            {location.pathname === '/vault' && (
              <div className="hidden md:block">
                <div className="flex items-center justify-between px-2 mb-3">
                  <h3 className="text-[10px] font-bold text-text-hint uppercase tracking-widest font-['Inter']">
                    PROJECT
                  </h3>
                  <div className="flex gap-2 items-center">
                    {isDeletingProjects && selectedProjectIds.size > 0 && (
                      <button
                        onClick={handleConfirmDelete}
                        className="text-[10px] bg-error text-white px-2 py-0.5 rounded font-medium hover:bg-error/80 transition-colors"
                      >
                        Confirm
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setIsDeletingProjects(!isDeletingProjects);
                        if (isDeletingProjects) setSelectedProjectIds(new Set());
                      }}
                      className={`${isDeletingProjects ? 'text-error' : 'text-text-hint hover:text-white'} transition-colors`}
                      title="Delete Projects"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => { setActiveParentId(null); setAddTypeModalOpen(true); }} className="text-text-hint hover:text-white transition-colors" title="Add Project">
                      <span className="material-symbols-outlined text-sm">add</span>
                    </button>
                  </div>
                </div>
                <div className="space-y-1">
                  {renderProjectTree(projectTree)}
                </div>
              </div>
            )}

            {location.pathname === '/home' && (
              <div className="hidden md:block">
                <div className="flex items-center justify-between px-2 mb-3">
                  <h3 className="text-[10px] font-bold text-text-hint uppercase tracking-widest font-['Inter']">
                    MY VAULTS
                  </h3>
                </div>
                <div className="space-y-2 px-2">
                  
                  <div onClick={() => {
                    chrome.storage.local.set({ currentVault: 'dev' }, () => {
                      window.dispatchEvent(new CustomEvent('vaultChanged', { detail: 'dev' }));
                      navigate('/vault');
                    });
                  }} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-surface-container cursor-pointer group transition-colors border border-transparent hover:border-border-muted">
                    <div className="p-1.5 bg-blue-900/30 text-blue-400 rounded-md">
                      <Code size={16} />
                    </div>
                    <span className="text-sm font-medium text-text-muted group-hover:text-white transition-colors">Dev Snippets</span>
                  </div>

                  <div onClick={() => {
                    chrome.storage.local.set({ currentVault: 'ai' }, () => {
                      window.dispatchEvent(new CustomEvent('vaultChanged', { detail: 'ai' }));
                      navigate('/vault');
                    });
                  }} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-surface-container cursor-pointer group transition-colors border border-transparent hover:border-border-muted">
                    <div className="p-1.5 bg-purple-900/30 text-purple-400 rounded-md">
                      <Terminal size={16} />
                    </div>
                    <span className="text-sm font-medium text-text-muted group-hover:text-white transition-colors">AI Prompts</span>
                  </div>

                  <div onClick={() => {
                    chrome.storage.local.set({ currentVault: 'links' }, () => {
                      window.dispatchEvent(new CustomEvent('vaultChanged', { detail: 'links' }));
                      navigate('/vault');
                    });
                  }} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-surface-container cursor-pointer group transition-colors border border-transparent hover:border-border-muted">
                    <div className="p-1.5 bg-emerald-900/30 text-emerald-400 rounded-md">
                      <Globe size={16} />
                    </div>
                    <span className="text-sm font-medium text-text-muted group-hover:text-white transition-colors">Saved Links</span>
                  </div>

                  <div onClick={() => {
                    chrome.storage.local.set({ currentVault: 'tools' }, () => {
                      window.dispatchEvent(new CustomEvent('vaultChanged', { detail: 'tools' }));
                      navigate('/vault');
                    });
                  }} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-surface-container cursor-pointer group transition-colors border border-transparent hover:border-border-muted">
                    <div className="p-1.5 bg-amber-900/30 text-amber-400 rounded-md">
                      <LayoutDashboard size={16} />
                    </div>
                    <span className="text-sm font-medium text-text-muted group-hover:text-white transition-colors">Tool Configs</span>
                  </div>

                </div>
              </div>
            )}


          </nav>

          {/* Bottom Sidebar Profile & Actions (Original style updated) */}
          <div className="p-4 border-t border-border-muted flex flex-col gap-4 bg-(--color-surface)">
            {currentUser ? (
              <>
                <div className="flex items-center space-x-3 min-w-0 w-full">
                  {userProfile?.photoURL ? (
                    <OptimizedImage src={userProfile.photoURL.replace('=s96-c', '=s32-c')} alt="Profile" className="w-10 h-10 rounded-full shrink-0 border border-border-muted" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-surface-container-high flex items-center justify-center shrink-0 border border-border-muted">
                      <span className="text-sm font-bold text-white">{userProfile?.displayName?.charAt(0) || 'U'}</span>
                    </div>
                  )}
                  <div className="min-w-0 flex-1 hidden md:block">
                    <p className="text-sm font-medium truncate text-white" title={userProfile?.displayName}>{userProfile?.displayName}</p>
                    <p className="text-[10px] text-dev-blue font-bold truncate">DevIT Creds: {userProfile?.devItCreds || 0}</p>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex flex-col gap-2 w-full">
                <span className="text-xs text-text-hint font-medium text-center hidden md:block">Free Tier</span>
                <Link to="/login" className="text-sm font-bold text-center text-bg-main bg-dev-blue hover:bg-blue-400 py-2 rounded-lg transition-colors">Sign In</Link>
              </div>
            )}
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="flex-1 flex flex-col relative overflow-hidden bg-linear-to-br from-bg-main to-surface-container-lowest">
          <div className="flex-1 overflow-auto p-4 lg:p-6 custom-scrollbar flex flex-col">
            {/* We maintain the Outlet for DashboardHomeView / VaultView / etc */}
            <div className="flex-1">
              <Outlet />
            </div>

            {/* Footer added based on feedback */}
            <footer className="mt-8 pt-4 border-t border-border-muted flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-text-hint pb-2">
              <span className="text-text-muted">&copy; {new Date().getFullYear()} DevIT Systems</span>
              <div className="flex flex-wrap items-center justify-center md:justify-end gap-x-4 gap-y-2 max-w-xl">
                <Link to="/about" className="hover:text-dev-blue transition-colors text-blue-400/80">About Company</Link>
                <Link to="/privacy" className="hover:text-dev-blue transition-colors text-blue-400/80">Privacy Policy</Link>
                <Link to="/terms" className="hover:text-dev-blue transition-colors text-blue-400/80">Terms of Service</Link>
                <Link to="/cookies" className="hover:text-dev-blue transition-colors text-blue-400/80">Cookie Policy</Link>
                <Link to="/license" className="hover:text-dev-blue transition-colors text-blue-400/80">License Agreement</Link>
                <Link to="/contact" className="hover:text-dev-blue transition-colors text-blue-400/80">Contact Developer</Link>
                <Link to="/security" className="hover:text-dev-blue transition-colors text-blue-400/80">Security</Link>
                <Link to="/changelog" className="hover:text-dev-blue transition-colors text-blue-400/80">Changelog</Link>
              </div>
            </footer>
          </div>
        </main>
      </div>

      {/* Modals for Folder Hierarchy */}
      {addTypeModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-surface-container border border-border-muted rounded-xl p-6 w-full max-w-sm shadow-2xl">
            <h3 className="text-lg font-bold text-white mb-4">Add New Item</h3>
            <p className="text-sm text-text-muted mb-6">What would you like to add to this folder?</p>
            <div className="flex flex-col gap-3">
              <button 
                onClick={() => {
                  setAddTypeModalOpen(false);
                  alert('Adding a SnipIT (Routing to snippet creation...)');
                }}
                className="flex items-center justify-center gap-2 p-3 bg-dev-blue hover:bg-blue-500 text-white rounded-lg transition-colors font-medium"
              >
                <FilePlus className="w-4 h-4" />
                SnipIT
              </button>
              <button 
                onClick={() => {
                  setAddTypeModalOpen(false);
                  setAddFolderModalOpen(true);
                }}
                className="flex items-center justify-center gap-2 p-3 bg-surface-container-high hover:bg-surface-container-highest text-white border border-border-muted rounded-lg transition-colors font-medium"
              >
                <FolderPlus className="w-4 h-4" />
                Sub Folder
              </button>
              <button 
                onClick={() => setAddTypeModalOpen(false)}
                className="mt-2 text-sm text-text-hint hover:text-white transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {addFolderModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-surface-container border border-border-muted rounded-xl p-6 w-full max-w-sm shadow-2xl">
            <h3 className="text-lg font-bold text-white mb-4">New Folder</h3>
            <form onSubmit={handleAddFolderSubmit}>
              <input
                type="text"
                autoFocus
                placeholder="Folder Name"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                className="w-full bg-surface-container-low border border-border-muted rounded-lg px-4 py-2 text-white mb-6 focus:outline-none focus:border-dev-blue"
              />
              <div className="flex gap-3">
                <button 
                  type="button"
                  onClick={() => { setAddFolderModalOpen(false); setNewFolderName(''); setActiveParentId(null); }}
                  className="flex-1 p-2 text-text-muted hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  disabled={!newFolderName.trim()}
                  className="flex-1 p-2 bg-dev-blue hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
